import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { fmtDateTime } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { EmptyState, PageHeader, QueryState } from "@/components/common";

const PAGE = 50;

export default function Activity() {
  const [onlyKey, setOnlyKey] = useState(false);
  const [page, setPage] = useState(0);
  const { can, role, rolesReady } = useWorkspace();
  const allowed = can("view_audit");
  const q = useWsQuery(`/activity?limit=${PAGE}&skip=${page * PAGE}${onlyKey ? "&consequential=true" : ""}`, { enabled: allowed });
  if (role && rolesReady && !allowed) return (
    <div><PageHeader eyebrow="Audit" title="Activity" />
      <div data-testid="activity-no-permission"><EmptyState icon={ShieldCheck} title="The audit log is restricted" description="Only owners, admins and approvers can view workspace activity." /></div></div>
  );
  return (
    <div>
      <PageHeader eyebrow="Audit" title="Activity" description="An append-only record of actions in this workspace, including every consequential decision."
        actions={<button data-testid="activity-filter-consequential" onClick={() => { setOnlyKey(!onlyKey); setPage(0); }} className={`cq-btn ${onlyKey ? "cq-btn-primary" : "cq-btn-ghost"}`}><ShieldCheck size={14} /> Consequential only</button>} />
      <QueryState q={q} empty={<EmptyState title="No activity yet" />}>
        {(d) => !d.items.length ? <EmptyState title="No activity in this view" /> : (<>
          <div className="cq-card divide-y divide-[#172342]" data-testid="activity-list">
            {d.items.map((a) => (
              <div key={a.id} data-testid={`activity-row-${a.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                <span className="font-mono text-[11px] text-[#7C8BAD] w-36 shrink-0">{fmtDateTime(a.created_at)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F7FAFF]">{a.summary}</div>
                  <div className="text-[11px] text-[#7C8BAD] mt-0.5 font-mono">{a.actor_name} · {a.action}{a.metadata?.comment ? ` · “${a.metadata.comment}”` : ""}</div>
                </div>
                <div className="flex gap-2">
                  {a.consequential && <span className="cq-chip text-[#22E6F2] border-[#22E6F2]/30 bg-[#22E6F2]/10">Consequential</span>}
                  {a.is_demo && <span className="cq-chip text-[#F4B860] border-[#F4B860]/30 bg-[#F4B860]/10">Demo</span>}
                </div>
              </div>))}
          </div>
          <div className="mt-4 flex items-center justify-between text-xs text-[#7C8BAD]">
            <span data-testid="activity-total">{d.total} events</span>
            <div className="flex gap-2">
              <button data-testid="activity-prev" disabled={page === 0} onClick={() => setPage(page - 1)} className="cq-btn cq-btn-ghost !py-1">Previous</button>
              <button data-testid="activity-next" disabled={(page + 1) * PAGE >= d.total} onClick={() => setPage(page + 1)} className="cq-btn cq-btn-ghost !py-1">Next</button>
            </div>
          </div>
        </>)}
      </QueryState>
    </div>
  );
}
